import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PractitionRole } from '../_models/practition-role.model';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { environment } from 'src/environments/environment';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';

@Injectable({ providedIn: 'root' })
export class PractitionRoleService {
  private authToken;
  private companyId;

  constructor(
    private httpClient: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  createPractitionRole(_PractitionRole: PractitionRole) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      resourceType: 'PractitionerRole',
      active: _PractitionRole.active,
      period: {
        start: _PractitionRole.period_start,
        end: _PractitionRole.period_end,
      },
      practitioner: {
        id: _PractitionRole.doctor_id,
        resourceType: 'Practitioner',
      },
      organization: {
        id: this.companyId.toString(),
        resourceType: 'Organization',
      },
      code: [
        {
          coding: [
            {
              code: _PractitionRole.code != null ? _PractitionRole.code[`concept`][`id`] : null,
              display:
                _PractitionRole.code != null
                  ? _PractitionRole.code[`concept`][`fsn`][`term`]
                  : null,
            },
          ],
        },
      ],
      specialty: [
        {
          coding: [
            {
              code:
                _PractitionRole.specialty != null
                  ? _PractitionRole.specialty[`concept`][`id`]
                  : null,
              display:
                _PractitionRole.specialty != null
                  ? _PractitionRole.specialty[`concept`][`fsn`][`term`]
                  : null,
            },
          ],
        },
      ],
      availableTime: _PractitionRole.availableTime,
      notAvailable: [
        {
          description: _PractitionRole.notAvailable_description,
          during: {
            start: _PractitionRole.notAvailable_during_start,
            end: _PractitionRole.notAvailable_during_end,
          },
        },
      ],
      availabilityExceptions:
        _PractitionRole.availabilityExceptions !== ''
          ? _PractitionRole.availabilityExceptions
          : null,
      healthcareService: [
        // {
        //   id: _PractitionRole.healthcareService[`id`],
        //   resourceType: 'HealthcareService'
        // }
      ],
    };

    return this.httpClient.post<PractitionRole>(
      'https://aplos2.aidbox.app/PractitionerRole',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    );
  }

  getAllPractitionRoles(
    pageIndex?: number,
    pageSize?: number,
    searchInput?: any,
    isActive?: boolean,
    id?: number,
  ): Observable<PractitionRole[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.httpClient.post<PractitionRole[]>(
      environment.USER_BASE + '/all-PractitionRoles',
      { pageIndex, pageSize, searchData: searchInput, isActive, id },
      { headers: httpHeaders },
    );
  }

  deletePractitionRole(id?: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.httpClient.delete<any>('https://aplos2.aidbox.app/PractitionerRole/' + id, {
      headers: httpHeaders,
    });
  }

  findPractitionRoles(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const pageSize = queryParams.pageSize;
    const pageIndex = queryParams.pageNumber;
    const active = queryParams.filter.active;
    const pract = queryParams.filter.practitioner_idd;
    const params = new HttpParams({
      fromString:
        'practitioner=' +
        pract +
        '&_count=' +
        pageSize +
        '&page=' +
        pageIndex +
        '&organization=' +
        this.companyId +
        '&active=' +
        active,
    });
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.httpClient.get<QueryResultsModel>('https://aplos2.aidbox.app/PractitionerRole', {
      headers,
      params,
    });
  }

  searchCode(search_term: string): Observable<any> {
    return this.httpClient.get<any>(
      'https://browser.ihtsdotools.org/snowstorm/snomed-ct/browser/MAIN/2020-07-31/descriptions?&limit=10&term=' +
        search_term +
        '&active=true&conceptActive=true&lang=english&semanticTags=person',
    );
  }

  searchHealth(search_term: string): Observable<any> {
    const params = new HttpParams({ fromString: 'name=' + search_term });
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.httpClient.get<any>('https://aplos2.aidbox.app/PractitionerRole', {
      headers,
      params,
    });
  }

  searchPractitionerRoles(term: string): Observable<any> {
    const params = new HttpParams({
      fromString: 'specialty=' + term + '&organization=' + this.companyId,
    });
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = 'https://aplos2.aidbox.app/PractitionerRole';
    return this.httpClient.get<any>(url, { headers, params });
  }

  updatePractitionRole(_PractitionRole: PractitionRole) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      resourceType: 'PractitionerRole',
      active: _PractitionRole.active,
      period: {
        start: _PractitionRole.period_start,
        end: _PractitionRole.period_end,
      },
      practitioner: {
        id: _PractitionRole.doctor_id,
        resourceType: 'Practitioner',
      },
      organization: {
        id: this.companyId.toString(),
        resourceType: 'Organization',
      },
      code: [
        {
          coding: [
            {
              code: _PractitionRole.code != null ? _PractitionRole.code[`concept`][`id`] : null,
              display:
                _PractitionRole.code != null
                  ? _PractitionRole.code[`concept`][`fsn`][`term`]
                  : null,
            },
          ],
        },
      ],
      specialty: [
        {
          coding: [
            {
              code:
                _PractitionRole.specialty != null
                  ? _PractitionRole.specialty[`concept`][`id`]
                  : null,
              display:
                _PractitionRole.specialty != null
                  ? _PractitionRole.specialty[`concept`][`fsn`][`term`]
                  : null,
            },
          ],
        },
      ],
      availableTime: _PractitionRole.availableTime,
      notAvailable: [
        {
          description: _PractitionRole.notAvailable_description,
          during: {
            start: _PractitionRole.notAvailable_during_start,
            end: _PractitionRole.notAvailable_during_end,
          },
        },
      ],
      availabilityExceptions:
        _PractitionRole.availabilityExceptions !== ''
          ? _PractitionRole.availabilityExceptions
          : null,
      healthcareService: [
        // {
        //   id: _PractitionRole.healthcareService['id'],
        //   resourceType: 'HealthcareService'
        // }
      ],
    };

    return this.httpClient.put<PractitionRole>(
      'https://aplos2.aidbox.app/PractitionerRole/' + _PractitionRole.id,
      body,
      {
        headers: httpHeaders,
      },
    );
  }
}
